import { useMutation, useQuery } from "@tanstack/react-query";

import { MDS_BASE_URL } from "constants/config";
import { HOUR, QUERY_RETRY_ATTEMPTS } from "constants/queryCacheTimes";
import { normalizeCountryMappings } from "utils/countryMappings";

const uploadCsv = async ({ file, selectedScriptId, uploaderUserId }) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/upload-file?uploaded_by=${uploaderUserId}&selected_script_id=${selectedScriptId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY}`,
  };

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to upload file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while discarding the order."
    );
  }
};

export const useUploadCsv = () => {
  return useMutation({ mutationFn: uploadCsv });
};

const fetchProcessedFile = async (fileId) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/download-processed-file/${fileId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch processed file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the processed file."
    );
  }
};

export const useFetchProcessedFile = (fileId) => {
  const query = useQuery({
    queryKey: ["processedFile", fileId],
    queryFn: () => fetchProcessedFile(fileId),
    enabled: !!fileId,
  });
  return {
    ...query,
    fetchProcessedFile,
  };
};

const fetchCountryMappings = async () => {
  if (!MDS_BASE_URL) {
    throw new Error("MDS base URL is not configured.");
  }

  const url = new URL(`${MDS_BASE_URL}/base/get-all-mappings`);
  url.searchParams.set("resource_type", "country");

  const response = await fetch(url, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch country mappings. Status: ${response.status}`
    );
  }

  const data = await response.json();
  return normalizeCountryMappings(data?.items || []);
};

export const useCountriesMappings = () => {
  return useQuery({
    queryKey: ["mds", "country"],
    queryFn: fetchCountryMappings,
    staleTime: 12 * HOUR,
    cacheTime: 12 * HOUR,
    retry: QUERY_RETRY_ATTEMPTS,
    refetchOnMount: true,
  });
};

const generateOtp = async (phoneNumberAndCaptcha) => {
  const url = new URL(
    `${process.env.REACT_APP_COMMSERVICE_URL}/api/v3/generate-otp`
  );
  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };
  const payload = {
    isd_code: phoneNumberAndCaptcha.isd_code,
    phone: phoneNumberAndCaptcha.phone,
    tenant_id: 1,
    product_id: 300,
    captcha_token: phoneNumberAndCaptcha.captcha_token,
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again."
    );
  }
};

export const useGenerateOtp = () => {
  return useMutation({ mutationFn: generateOtp });
};
